import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  MutableRefObject,
} from 'react';

import {
  CardSubWrapper,
  CardWrapper,
  HeadingWrapper,
} from 'admin/components/wrappers';
import { RideShowBackNoCell } from 'admin/pages/TeamIndividual/components/TeamRiderListing/RiderShowbackNoCell';
import DivisionCell from 'app/components/CustomDataGrid/Cells/DivisionCell';
import RiderResultName from 'app/components/CustomDataGrid/Cells/RiderResultName';
import TeamResultName from 'app/components/CustomDataGrid/Cells/TeamResultName';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import {
  GroupedShowRiderPointWrapper,
  RiderPoint,
  RiderSplitClass,
} from 'services/openapi';
import styled from 'styled-components';
import { ShowsScoreActionCell } from 'app/components/CustomDataGrid/Cells/ShowsScoreActionCell';
import { TitleCardWrapper } from '../../ScoreBoardPage';
import { PageMainLabel } from 'app/components/SHLabel';
import NoDataFound from 'app/components/NoDataFound';
import { AdminDatePicker } from 'app/components/AdminDatePicker';
import { AdminCheckBox } from 'app/components/AdminCheckbox';
import { isoString } from 'utils/common';
import SimpleReactValidator from 'simple-react-validator';
import YedaLogo from 'app/components/admin/sidebar/assets/img/yeda-logo.png';

// ── Sortable header cell ──────────────────────────────────────────────────────
const SortableHeaderCell = ({
  label,
  apiField,
  sortBy,
  sortOrder,
  onSortChange,
}: {
  label: string;
  apiField: string;
  sortBy: string;
  sortOrder: number;
  onSortChange: (sortBy: string, sortOrder: number) => void;
}) => {
  const isActive = sortBy === apiField;
  const handleClick = () => {
    const newOrder = isActive && sortOrder === 1 ? 0 : 1;
    onSortChange(apiField, newOrder);
  };
  return (
    <SortHeaderSpan onClick={handleClick}>
      {label}
      {isActive ? (
        sortOrder === 1 ? (
          <SortArrow>&nbsp;▲</SortArrow>
        ) : (
          <SortArrow>&nbsp;▼</SortArrow>
        )
      ) : (
        <SortArrow style={{ opacity: 0.35 }}>&nbsp;⬍</SortArrow>
      )}
    </SortHeaderSpan>
  );
};

// ---- Per-rider scratch cell (own local state so parent re-renders don't reset it) ---
type ScratchCellProps = {
  rider: any;
  onScratchDateChange: (item: RiderSplitClass) => void;
  simpleValidator: SimpleReactValidator;
  resetKey: number;
  /** Stable function: register a callback to be called when validation is triggered */
  subscribeToValidation: (fn: () => void) => () => void;
};

const ScratchCell = React.memo(
  ({
    rider,
    onScratchDateChange,
    simpleValidator,
    resetKey,
    subscribeToValidation,
  }: ScratchCellProps) => {
    const [scratch, setScratch] = React.useState<boolean>(
      rider.scratch ?? false,
    );
    const [scratchDate, setScratchDate] = React.useState<string | null>(
      rider.scratchDate ?? null,
    );
    // Tracks how many times validation has been triggered so we can re-render
    const [validationRevision, setValidationRevision] = React.useState(0);

    // Register with parent so we get notified without prop changes recreating columns
    React.useEffect(() => {
      const unsubscribe = subscribeToValidation(() =>
        setValidationRevision(r => r + 1),
      );
      return unsubscribe;
      // subscribeToValidation is stable (useCallback with [] deps)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribeToValidation]);

    // Reset to server values when the parent triggers a global cancel
    React.useEffect(() => {
      setScratch(rider.scratch ?? false);
      setScratchDate(rider.scratchDate ?? null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);

    // Show validation message for this cell when validation is triggered
    const scratchRef = React.useRef(scratch);
    const scratchDateRef = React.useRef(scratchDate);
    scratchRef.current = scratch;
    scratchDateRef.current = scratchDate;
    React.useEffect(() => {
      if (
        validationRevision > 0 &&
        scratchRef.current &&
        !scratchDateRef.current
      ) {
        simpleValidator.showMessageFor('Scratch Date');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validationRevision]);

    const dateValue = scratchDate ? new Date(scratchDate) : undefined;

    const handleCheckboxChange = (event: any) => {
      const checked: boolean = event.value;
      const newScratchDate = checked ? scratchDate : null;
      setScratch(checked);
      if (!checked) setScratchDate(null);
      onScratchDateChange({
        showRiderId: rider.showRiderId,
        riderClass: rider.riderClass,
        split: rider.split,
        scratch: checked,
        scratchDate: newScratchDate,
      });
    };

    const handleDateChange = (evt: any) => {
      const date: Date | null = evt.value ?? null;
      const newScratchDate = date ? isoString(date) : null;
      setScratchDate(newScratchDate);
      onScratchDateChange({
        showRiderId: rider.showRiderId,
        riderClass: rider.riderClass,
        split: rider.split,
        scratch,
        scratchDate: newScratchDate,
      });
    };

    return (
      <ScratchTd>
        <ScratchCellWrapper>
          <AdminCheckBox
            onChange={event => {
              if (event) handleCheckboxChange(event);
            }}
            value={scratch}
            label="Scratch"
            className="scratch-checkbox"
          />
          <DateFieldWrapper>
            <AdminDatePicker
              placeholder="Scratch Date"
              value={dateValue}
              onChange={evt => handleDateChange(evt)}
              className="field-style create-date"
              disabled={!scratch}
              inputDisable
              validations={scratch ? 'required' : ''}
              validator={simpleValidator}
              hideStar
            />
          </DateFieldWrapper>
        </ScratchCellWrapper>
      </ScratchTd>
    );
  },
);

const PrintTable: React.FC<{
  riderShowList: GroupedShowRiderPointWrapper | undefined;
}> = ({ riderShowList }) => {
  return (
    <PrintTableWrapper>
      <div className="print-logo">
        <img src={YedaLogo} alt="YEDA" />
      </div>
      <table className="print-table">
        <thead>
          <tr className="group-header-row">
            <th colSpan={7} className="section-header">
              Rider Information
            </th>
          </tr>
          <tr className="col-header-row">
            <th>Team</th>
            <th>Rider</th>
            <th>Back #</th>
            <th>Division</th>
            <th>Total Points</th>
            <th>Scratch Date</th>
          </tr>
        </thead>
        <tbody>
          {riderShowList?.showClasses?.map((cls, idx) => (
            <React.Fragment key={idx}>
              <tr className="class-row">
                <td colSpan={7} className="class-label">
                  Class {cls.classNumber} &ndash;{' '}
                  {Array.from(new Set(cls.riders?.map(r => r.group)))?.join(
                    ', ',
                  )}{' '}
                  {cls.category?.name} {cls.division?.name} {cls.class?.name}
                </td>
              </tr>
              {cls.riders?.map((rider, rIdx) => (
                <tr key={rIdx} className={rIdx % 2 === 1 ? 'row-alt' : ''}>
                  <td>{rider.teamName}</td>
                  <td>{rider.riderName}</td>
                  <td>{rider.riderBackNo}</td>
                  <td className="division-cell">{rider.division}</td>
                  <td>
                    <input
                      className="points-input"
                      type="text"
                      defaultValue={
                        rider.totalPoints === 0 ||
                        rider.totalPoints === null ||
                        rider.totalPoints === undefined
                          ? ''
                          : rider.totalPoints
                      }
                      readOnly
                    />
                  </td>
                  <td className="scratch-date-cell">
                    {rider.scratchDate
                      ? (() => {
                          const d = new Date(rider.scratchDate);
                          return isNaN(d.getTime())
                            ? rider.scratchDate
                            : `${String(d.getMonth() + 1).padStart(
                                2,
                                '0',
                              )}-${String(d.getDate()).padStart(
                                2,
                                '0',
                              )}-${d.getFullYear()}`;
                        })()
                      : ''}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </PrintTableWrapper>
  );
};
type Props = {
  riderShowList: GroupedShowRiderPointWrapper | undefined;
  refreshFunction: () => void;
  onPointChange: (point: RiderPoint) => void;
  resetKey: number;
  onScratchDateChange: (item: RiderSplitClass) => void;
  simpleValidator: MutableRefObject<SimpleReactValidator>;
  triggerValidation: boolean;
  sortBy: string;
  sortOrder: number;
  onSortChange: (sortBy: string, sortOrder: number) => void;
};

const RiderInformationTable = ({
  riderShowList,
  refreshFunction,
  onPointChange,
  resetKey,
  onScratchDateChange,
  simpleValidator,
  triggerValidation,
  sortBy,
  sortOrder,
  onSortChange,
}: Props) => {
  // Subscribers set: ScratchCells register here to be notified of validation
  const validationSubscribersRef = useRef<Set<() => void>>(new Set());

  // Stable subscription function – never changes reference
  const subscribeToValidation = useCallback((fn: () => void) => {
    validationSubscribersRef.current.add(fn);
    return () => {
      validationSubscribersRef.current.delete(fn);
    };
  }, []);

  // When parent signals validation, notify all mounted ScratchCells
  useEffect(() => {
    validationSubscribersRef.current.forEach(fn => fn());
  }, [triggerValidation]);

  const RiderColumn = useMemo(
    () => [
      {
        field: 'teamId',
        title: 'Team',
        cell: e => <TeamResultName e={e} />,
        headerCell: () => (
          <SortableHeaderCell
            label="Team"
            apiField="team"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
          />
        ),
      },
      {
        field: 'riderName',
        title: 'Rider',
        cell: e => <RiderResultName e={e} />,
        headerCell: () => (
          <SortableHeaderCell
            label="Rider"
            apiField="rider"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
          />
        ),
      },
      {
        field: 'name',
        title: 'Back#',
        cell: e => <RideShowBackNoCell e={e} />,
        width: '100',
      },
      {
        field: 'name',
        title: 'Division',
        cell: e => <DivisionCell e={e} />,
        width: '150',
      },
      {
        field: 'totalPoints',
        title: 'Total Points',
        cell: e => (
          <ShowsScoreActionCell
            e={e}
            onPointChange={onPointChange}
            resetKey={resetKey}
          />
        ),
        width: '120',
      },
      {
        field: 'scratch',
        title: 'Scratch Date',
        cell: e => (
          <ScratchCell
            rider={e.dataItem}
            onScratchDateChange={onScratchDateChange}
            simpleValidator={simpleValidator.current}
            resetKey={resetKey}
            subscribeToValidation={subscribeToValidation}
          />
        ),
        width: '280',
      },
    ],
    [
      onPointChange,
      onScratchDateChange,
      resetKey,
      simpleValidator,
      subscribeToValidation,
      sortBy,
      sortOrder,
      onSortChange,
    ],
  );

  return (
    <>
      <ScreenOnlyWrapper>
        <CardWrapper style={{ paddingTop: '15px' }}>
          <CardSubWrapper className="d-flex"></CardSubWrapper>

          <GridWrapper>
            {riderShowList?.showClasses?.map((item, index) => (
              <div key={index}>
                <TitleCardWrapper className="title-card-wrapper">
                  <HeadingWrapper
                    className="heading-wrapper enter-results-heading-wrapper"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <PageMainLabel
                      className="label-wrapper"
                      style={{ flex: 1, textAlign: 'center' }}
                    >
                      Class {item.classNumber} &ndash;{' '}
                      {Array.from(
                        new Set(item.riders?.map(r => r.group)),
                      )?.join(', ')}
                      : {item.category?.name} {item.division?.name}{' '}
                      {item?.class?.name}
                    </PageMainLabel>
                    <PageMainLabel
                      className="label-wrapper"
                      style={{
                        fontSize: '14px',
                        color: '#666',
                        paddingRight: '15px',
                      }}
                    >
                      Split {item.splitNumber} ({item.riders?.length ?? 0} rider
                      {item.riders?.length === 1 ? '' : 's'})
                    </PageMainLabel>
                  </HeadingWrapper>
                </TitleCardWrapper>
                <CustomDataGrid
                  data={item?.riders ?? []}
                  columns={RiderColumn}
                />
              </div>
            ))}
            {riderShowList?.showClasses?.length === 0 && <NoDataFound />}
          </GridWrapper>
        </CardWrapper>
      </ScreenOnlyWrapper>
      <PrintTable riderShowList={riderShowList} />
    </>
  );
};

export default RiderInformationTable;

// ── Styled components ─────────────────────────────────────────────────────────

const SortHeaderSpan = styled.span`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  font-weight: 600;
  &:hover {
    opacity: 0.75;
  }
`;

const SortArrow = styled.span`
  font-size: 10px;
  line-height: 1;
`;

const ScratchTd = styled.td``;

const ScratchCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .scratch-checkbox label {
    display: block !important;
    white-space: nowrap;
  }
`;

const DateFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;

  .custom-calendar-wrapper {
    min-height: auto;

    .k-input-solid {
      height: 31px;
      max-width: 180px;
    }
  }
`;

// Screen-only wrapper – hidden when printing
const ScreenOnlyWrapper = styled.div`
  @media print {
    display: none;
  }
`;

// Print-only table wrapper
const PrintTableWrapper = styled.div`
  display: none;

  @media print {
    display: block;
    width: 100%;
  }

  .print-logo {
    display: none;

    @media print {
      display: block;
      margin-bottom: 10px;

      img {
        width: 85px;
        height: auto;
      }
    }
  }

  .print-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
    font-family: 'Inter', Arial, sans-serif;
    color: #000;
    page-break-inside: auto;
  }

  .print-table thead {
    display: table-header-group;
  }

  .print-table tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  .print-table th,
  .print-table td {
    border: 1px solid #ccc;
    padding: 3px 6px;
    text-align: left;
    vertical-align: middle;
  }

  .section-header {
    background-color: #d9e1f2;
    font-weight: 700;
    text-align: center;
    font-size: 11px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .col-header-row th {
    background-color: #eaecf0;
    font-weight: 600;
    text-align: center;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .class-row {
    page-break-inside: avoid;
  }

  .class-row td {
    background-color: #dce6f1;
    font-weight: 600;
    font-size: 10px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .class-label {
    text-align: left;
  }

  .split-label {
    text-align: right;
    font-style: italic;
  }

  .row-alt td {
    background-color: #f7f9fc;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .division-cell {
    color: #1a56db;
    font-weight: 600;
  }

  .scratch-cell {
    text-align: center;
    font-weight: 700;
    color: #c0392b;
  }

  .scratch-date-cell {
    text-align: center;
    white-space: nowrap;
  }

  .points-input {
    width: 50px;
    border: 1px solid #aaa;
    padding: 1px 3px;
    font-size: 10px;
    text-align: center;
  }

  .save-stats-btn {
    background-color: #1a56db;
    color: #fff;
    border: none;
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 9px;
    cursor: pointer;
    white-space: nowrap;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const GridWrapper = styled.div`
  .k-grid-header tr th {
    border-top: 1px solid #eaecf0;
  }

  /* .k-grid-container.k-grid-content tr td:nth-last-child(1) {
    padding: 0px 24px 0px 0px !important;
  } */

  .k-grid-content tr {
    &:hover {
      td {
        background: #f0f0f0 !important;
      }
    }
  }

  .k-grid-content tr td {
    cursor: pointer;
  }

  .k-grid-content tr td.team-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  .k-grid-container {
    height: auto !important;
  }

  .k-grid-container.k-grid-content {
    height: auto !important;
    max-height: none !important;
  }

  .k-grid {
    height: auto !important;
  }

  .k-grid-content {
    min-height: 0 !important;
  }
`;
